// src/lib/calculation/educationCalculation.ts
//
// BekalNanti — Milestone P5: Pure Deterministic Calculation Engine
// Strictly deterministic, pure mathematical functions. Zero external dependencies.
// Does NOT provide financial advice, make subjective judgments, or invent assumptions.

import type { CostFrequency } from '../costFunding'
import type {
  CalculationInput,
  CalculationResult,
  CalculationStatus,
  NormalizedCostItem,
  CategorizedFunding,
} from './types'

/**
 * Baseline conservative annual education inflation planning assumption.
 * Provenance: Standard conservative planning parameter for Indonesian higher education
 * cost growth (historical BPS/industry range: 5% - 10%).
 * Strictly represents a "planning assumption" (asumsi perencanaan), NOT an objective
 * macroeconomic forecast. Selectable alternatives (0%, 3%, 5%, 7%, 10%) allow users to
 * simulate various family scenarios.
 */
export const DEFAULT_ANNUAL_INFLATION_RATE = 0.05 // 5% annual planning cost growth baseline

/**
 * Determine the duration multiplier for a cost item given its frequency
 * and the canonical study duration in months.
 */
export function getDurationMultiplier(frequency: CostFrequency, durationMonths: number): number {
  if (!durationMonths || durationMonths <= 0) return 0

  switch (frequency) {
    case 'one_off':
      return 1
    case 'per_year':
      return durationMonths / 12
    case 'per_semester':
      return durationMonths / 6
    case 'per_month':
      return durationMonths
    case 'total_program':
      return 1
    default:
      return 1
  }
}

/**
 * Calculate future value under compound annual inflation.
 * FV = PV * (1 + rate)^years
 */
export function calculateFutureValue(presentValue: number, annualRate: number, years: number): number {
  if (presentValue <= 0) return 0
  if (annualRate === 0 || years <= 0) return Math.round(presentValue)
  const multiplier = Math.pow(1 + annualRate, years)
  return Math.round(presentValue * multiplier)
}

/**
 * Categorize funding sources conservatively and deterministically.
 *
 * Semantic Mapping Rules (P5.1 Verified):
 * 1. UNKNOWN: amount is null/undefined/NaN or certainty === 'unknown'.
 *    Strictly counted in unknownFundingCount and isolated into unknownSources.
 *    NEVER numerically counted in confirmed or estimated funding (even if status === 'confirmed').
 *
 * 2. CONFIRMED: amount > 0, certainty === 'known', AND (status === 'confirmed' || isConfirmed === true).
 *    Both certainty must be known AND status must be confirmed.
 *    Planned or applied sources are NEVER silently treated as confirmed funding.
 *
 * 3. ESTIMATED / PLANNED: amount > 0, but NOT meeting the strict CONFIRMED criteria
 *    (e.g. certainty === 'estimated', or status === 'planned', or status === 'applied').
 *    Participates in estimated/scenario funding without silently inflating confirmed capital.
 */
export function categorizeFundingSources(sources: CalculationInput['fundingSources']): CategorizedFunding {
  let confirmedFunding = 0
  let estimatedFunding = 0
  let unknownFundingCount = 0

  const confirmedSources: CalculationInput['fundingSources'] = []
  const estimatedSources: CalculationInput['fundingSources'] = []
  const unknownSources: CalculationInput['fundingSources'] = []

  for (const src of sources || []) {
    const isUnknown =
      src.certainty === 'unknown' ||
      src.amount === null ||
      src.amount === undefined ||
      isNaN(Number(src.amount))

    if (isUnknown) {
      unknownFundingCount++
      unknownSources.push(src)
      continue
    }

    const amt = Number(src.amount)
    if (amt <= 0) continue

    const isConfirmed =
      src.certainty === 'known' &&
      (src.status === 'confirmed' || src.isConfirmed === true)

    if (isConfirmed) {
      confirmedFunding += amt
      confirmedSources.push(src)
    } else {
      estimatedFunding += amt
      estimatedSources.push(src)
    }
  }

  return {
    confirmedFunding: Math.round(confirmedFunding),
    estimatedFunding: Math.round(estimatedFunding),
    totalPotentialFunding: Math.round(confirmedFunding + estimatedFunding),
    unknownFundingCount,
    confirmedSources,
    estimatedSources,
    unknownSources,
  }
}

/**
 * Pure Deterministic Calculation Engine.
 * Takes P2 Financial Reality + P3 Education Target + P4 Cost & Funding.
 * Produces transparent, reproducible calculation results without any side effects.
 */
export function calculateEducationPlan(input: CalculationInput): CalculationResult {
  const blockingReasons: string[] = []
  let status: CalculationStatus = 'calculable'

  const planningCurrency = input.planningCurrency || input.target.targetCurrency || 'IDR'
  const anchorYear = input.assumptions?.planningAnchorYear ?? new Date().getFullYear()
  const rawInflation = input.assumptions?.annualInflationRate ?? DEFAULT_ANNUAL_INFLATION_RATE

  // 1. Validate Inflation Rate
  if (isNaN(rawInflation) || rawInflation < 0) {
    blockingReasons.push('Asumsi inflasi tidak valid (tidak boleh bernilai negatif atau NaN)')
    status = 'invalid_input'
  }
  const inflationRate = isNaN(rawInflation) || rawInflation < 0 ? 0 : rawInflation

  // 2. Validate Study Duration
  const durationMonths = input.target.durationMonths ?? null
  if (durationMonths === null || durationMonths === undefined) {
    blockingReasons.push('Durasi studi (dalam bulan) belum ditentukan pada sasaran')
    if (status === 'calculable') status = 'incomplete_input'
  } else if (durationMonths <= 0 || isNaN(durationMonths)) {
    blockingReasons.push('Durasi studi harus berupa bilangan bulat positif lebih besar dari 0')
    status = 'invalid_input'
  }
  const validDurationMonths = durationMonths && durationMonths > 0 ? durationMonths : 0
  const studyDurationYears = validDurationMonths > 0 ? Number((validDurationMonths / 12).toFixed(2)) : 0

  // 3. Validate Target Year & Horizon
  let targetYear = input.target.targetYear ?? null
  if (targetYear === null && input.target.targetDate) {
    const parsedYear = new Date(input.target.targetDate).getFullYear()
    if (!isNaN(parsedYear) && parsedYear >= 2000) {
      targetYear = parsedYear
    }
  }

  let yearsUntilTarget = 0
  let monthsUntilTarget = 0

  if (targetYear === null) {
    blockingReasons.push('Tahun sasaran mulai studi belum ditentukan')
    if (status === 'calculable') status = 'incomplete_input'
  } else if (isNaN(targetYear)) {
    blockingReasons.push('Tahun sasaran tidak valid')
    status = 'invalid_input'
  } else if (targetYear < anchorYear) {
    blockingReasons.push(
      `Tahun sasaran (${targetYear}) sudah berlalu sebelum tahun perencanaan saat ini (${anchorYear})`
    )
    status = 'insufficient_horizon'
  } else {
    yearsUntilTarget = targetYear - anchorYear
    monthsUntilTarget = yearsUntilTarget * 12
  }

  // 4. Currency Consistency Check (Strictly NO silent FX conversion)
  const conflictingCurrencies = new Set<string>()
  if (input.target.targetCurrency && input.target.targetCurrency !== planningCurrency) {
    conflictingCurrencies.add(input.target.targetCurrency)
  }
  for (const item of input.costItems || []) {
    if (item.currency && item.currency !== planningCurrency) {
      conflictingCurrencies.add(item.currency)
    }
  }
  for (const src of input.fundingSources || []) {
    if (src.currency && src.currency !== planningCurrency) {
      conflictingCurrencies.add(src.currency)
    }
  }

  if (conflictingCurrencies.size > 0) {
    const list = Array.from(conflictingCurrencies).join(', ')
    blockingReasons.push(
      `Terdapat perbedaan mata uang (${list}) dengan mata uang perencanaan (${planningCurrency}). Konversi otomatis tidak dilakukan untuk mencegah distorsi kurs.`
    )
    status = 'currency_mismatch'
  }

  // 5. Cost Items Check
  const costItems = input.costItems || []
  if (costItems.length === 0) {
    blockingReasons.push('Belum ada komponen biaya pendidikan yang dipetakan (Tahap 4)')
    if (status === 'calculable') status = 'incomplete_input'
  }

  // Compute Inflation Multiplier
  const inflationMultiplier =
    yearsUntilTarget > 0 && inflationRate > 0 ? Math.pow(1 + inflationRate, yearsUntilTarget) : 1

  // 6. Normalize and Project Cost Items
  let totalNominalCost = 0
  let totalProjectedCost = 0
  let knownCost = 0
  let estimatedCost = 0
  let unknownCostCount = 0
  const normalizedItems: NormalizedCostItem[] = []

  for (const item of costItems) {
    const isUnknown = item.certainty === 'unknown' || item.amount === null || isNaN(Number(item.amount))

    if (isUnknown) {
      unknownCostCount++
      normalizedItems.push({
        id: item.id || `cost-${normalizedItems.length}`,
        name: item.name,
        category: item.category,
        frequency: item.frequency,
        certainty: 'unknown',
        currency: item.currency || planningCurrency,
        unitAmount: null,
        durationMultiplier: getDurationMultiplier(item.frequency, validDurationMonths),
        totalNominalCost: null,
        totalProjectedCost: null,
        isCalculable: false,
        notes: item.notes,
      })
      continue
    }

    const unitAmt = Number(item.amount)
    const mult = getDurationMultiplier(item.frequency, validDurationMonths)
    const itemNominal = Math.round(unitAmt * mult)
    const itemProjected = Math.round(itemNominal * inflationMultiplier)

    totalNominalCost += itemNominal
    totalProjectedCost += itemProjected

    if (item.certainty === 'known') {
      knownCost += itemNominal
    } else {
      estimatedCost += itemNominal
    }

    normalizedItems.push({
      id: item.id || `cost-${normalizedItems.length}`,
      name: item.name,
      category: item.category,
      frequency: item.frequency,
      certainty: item.certainty,
      currency: item.currency || planningCurrency,
      unitAmount: unitAmt,
      durationMultiplier: mult,
      totalNominalCost: itemNominal,
      totalProjectedCost: itemProjected,
      isCalculable: true,
      notes: item.notes,
    })
  }

  // 7. Categorize Funding
  const funding = categorizeFundingSources(input.fundingSources || [])

  // 8. Calculate Funding Gaps
  const gapAfterConfirmed = Math.max(0, totalProjectedCost - funding.confirmedFunding)
  const gapAfterEstimated = Math.max(0, totalProjectedCost - funding.totalPotentialFunding)
  const surplusAfterConfirmed = Math.max(0, funding.confirmedFunding - totalProjectedCost)
  const surplusAfterEstimated = Math.max(0, funding.totalPotentialFunding - totalProjectedCost)
  const hasSurplus = surplusAfterEstimated > 0

  // 9. Financial Reality Capacity
  const currentMonthlyNetCapacity = input.financialReality?.netMonthlyCapacity ?? 0
  const currentLiquidSavings = input.financialReality?.liquidSavings ?? 0

  // 10. Required Monthly Savings
  let requiredMonthlySavingConfirmedGap = 0
  let requiredMonthlySavingEstimatedGap = 0
  let isMathematicallyValid = false
  let formulaDescription = ''

  if (status === 'calculable') {
    if (monthsUntilTarget > 0) {
      isMathematicallyValid = true
      requiredMonthlySavingConfirmedGap = Math.ceil(gapAfterConfirmed / monthsUntilTarget)
      requiredMonthlySavingEstimatedGap = Math.ceil(gapAfterEstimated / monthsUntilTarget)
      formulaDescription = `Kebutuhan Tabungan Bulanan = Gap Pendanaan (${gapAfterEstimated}) / Sisa Horizon (${monthsUntilTarget} bulan)`
    } else if (yearsUntilTarget === 0) {
      isMathematicallyValid = true
      requiredMonthlySavingConfirmedGap = gapAfterConfirmed
      requiredMonthlySavingEstimatedGap = gapAfterEstimated
      formulaDescription = `Tahun sasaran adalah tahun ini (${anchorYear}). Kebutuhan harus disiapkan sekaligus.`
    }
  }

  // 11. Comparison with Current Capacity (Strictly Numerical, Zero Judgment)
  const requiredSaving = requiredMonthlySavingEstimatedGap
  const numericalDifference = currentMonthlyNetCapacity - requiredSaving
  const capacityRatio = requiredSaving > 0 ? Number((currentMonthlyNetCapacity / requiredSaving).toFixed(2)) : null

  // 12. Assumptions Metadata
  const timelineAssumptionNote =
    targetYear !== null
      ? `Horizon tabungan dihitung dari tahun perencanaan saat ini (${anchorYear}) ke tahun sasaran mulai studi (${targetYear}): ${yearsUntilTarget} tahun (${monthsUntilTarget} bulan) tanpa asumsi spekulatif bulan kalender. Durasi studi (${validDurationMonths} bulan / ${studyDurationYears} tahun) digunakan terpisah sebagai pengali komponen biaya berulang.`
      : 'Tahun sasaran belum ditentukan.'

  // 13. Status Messages & Labels
  const isCalculable = status === 'calculable'
  let statusLabel = 'Siap Dihitung'
  let statusMessage = 'Perhitungan deterministik berhasil diproses.'

  if (status === 'incomplete_input') {
    statusLabel = 'Data Belum Lengkap'
    statusMessage = 'Perhitungan belum dapat dijalankan karena beberapa data kunci masih kosong.'
  } else if (status === 'currency_mismatch') {
    statusLabel = 'Perbedaan Mata Uang'
    statusMessage = 'Perhitungan ditangguhkan karena terdapat komponen dengan mata uang berbeda.'
  } else if (status === 'invalid_input') {
    statusLabel = 'Input Tidak Valid'
    statusMessage = 'Terdapat parameter input yang tidak memenuhi kaidah matematika.'
  } else if (status === 'insufficient_horizon') {
    statusLabel = 'Horizon Tidak Memadai'
    statusMessage = 'Tahun sasaran berada di masa lalu.'
  }

  return {
    status,
    statusLabel,
    statusMessage,
    isCalculable,
    blockingReasons,
    planningCurrency,
    assumptions: {
      annualInflationRate: inflationRate,
      inflationPercentage: Math.round(inflationRate * 100),
      planningAnchorYear: anchorYear,
      targetYear,
      yearsUntilTarget,
      monthsUntilTarget,
      studyDurationMonths: validDurationMonths,
      studyDurationYears,
      inflationMultiplier: Number(inflationMultiplier.toFixed(6)),
      timelineAssumptionNote,
    },
    costs: {
      totalNominalCost,
      totalProjectedCost,
      knownCost,
      estimatedCost,
      unknownCostCount,
      normalizedItems,
    },
    funding,
    gap: {
      gapAfterConfirmedFunding: gapAfterConfirmed,
      gapAfterEstimatedFunding: gapAfterEstimated,
      surplusAfterConfirmed,
      surplusAfterEstimated,
      hasSurplus,
    },
    capacity: {
      currentMonthlyNetCapacity,
      currentLiquidSavings,
    },
    savingRequirement: {
      requiredMonthlySavingConfirmedGap,
      requiredMonthlySavingEstimatedGap,
      isMathematicallyValid,
      formulaDescription,
    },
    comparison: {
      requiredMonthlySaving: requiredSaving,
      currentMonthlyNetCapacity,
      numericalDifference,
      capacityRatio,
    },
  }
}
